/**
 * Inline transcript chips (WS6): ActivityChip (tool.start/result → subtle
 * "reading …" chips) and EntityChip / FileChip (typed entity mentions —
 * click stages a question about it).
 *
 * Round 2 (owner, on real sessions): "the reading 'files' lines … are not
 * adding any values to users. Maybe if you wanna show progress, keep them as
 * more general lines". The server now sends general phrases ("reading the
 * code", "following the path end to end"); this module makes sure a chip
 * shows ONE readable phrase and never a path, a line range or a raw tool id,
 * whatever a session's persisted history happens to carry.
 *
 * Round 5 (owner): "Each tool call can be expanded/collapsed. Just not with
 * too much detail that leaks internal details." So the chip is now a
 * disclosure when — and only when — there is one plain line worth showing
 * underneath it; a frame that says nothing past its verb stays a flat chip
 * with nothing to click. And the whole run of them collapses to a single
 * summary row (ActivityRun) once the turn is finished.
 */

import { useId, useState } from 'react';

import { formatElapsedMs, plural } from '../../lib/format';
import type { EntityRef } from '../../lib/frames';
import { useChatStore } from '../../stores/chatStore';
import { ToolGlyph } from './toolIcons';

/**
 * Friendly verb per agent tool (fallback: the tool name, de-underscored —
 * a raw id never reaches a chip). Covers the 11-tool registry (api.md §14,
 * AGENT_TOOL_NAMES) plus retired tool names that persisted display_json may
 * still carry.
 */
const TOOL_VERBS: Record<string, string> = {
  search_map: 'searching your codebase',
  get_node: 'reading your codebase',
  get_flow: 'reading a flow',
  read_file: 'reading',
  search_history: 'checking the history',
  get_live_infra: 'checking live infrastructure',
  get_runtime_map: 'reading how it runs',
  get_changes: 'checking what changed',
  get_symbol: 'reading a definition',
  get_schema: 'reading a schema',
  trace_route: 'tracing a request',
  replay_flow: 'replaying a real journey',
  simulate_schedule: 'working out a scheduled run',
  remember_reader: 'noting that for later',
  show_diagram: 'drawing a diagram',
  // Retired (pre-redesign display_json replays through here):
  propose_knowledge_update: 'making a note',
  canvas_focus: 'moving the camera',
  canvas_highlight: 'pointing at the map',
  canvas_step: 'stepping the flow',
  canvas_clear: 'clearing the canvas',
};

/** "brand-onboarding" → "Brand Onboarding" (chip copy only, never ids). */
export function humanizeSlug(slug: string): string {
  return slug
    .split('-')
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Server tool summaries the client rewrites into plain speech before they
// reach a chip (notation-leak audit 2026-08-25): the flow read's
// "slug (9 steps)" form and the step move's "step slug/s3" form.
const FLOW_SUMMARY_RE = /^([a-z0-9]+(?:-[a-z0-9]+)*)\s*\(\d+\s*steps?\)$/;
const STEP_SUMMARY_RE = /^step\s+([a-z0-9]+(?:-[a-z0-9]+)*)\/s\d+\b/;

/**
 * Strip s1/s2-style step ids from client-composed chip copy: `flow/s3`
 * suffixes, `s1-s2` ranges, and standalone `s4` tokens — but never a real
 * filename like `s3.py` (lookarounds keep dotted/word-joined forms intact).
 */
export function stripStepIds(text: string): string {
  return text
    .replace(/\/s\d+\b(?![\w.-])/g, '')
    .replace(/(?<![\w./-])s\d+(?:\s*[-–—]\s*s\d+)?(?![\w.-])/g, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Notation a reader gains nothing from: a file path or a bare `name.ext`
 * (any `:line` or `:symbol` suffix rides along) and a "lines 12-40" range.
 * A summary
 * carrying any of it loses its detail entirely — once the path is gone the
 * rest ("symbol", "read 3 files:") is a label for something no longer there.
 *
 * The shape is deliberately narrow so ordinary prose survives: a REST route
 * (`POST /{post_id}/publish`) has no extension, "read/write path" has no
 * dot, and "e.g." fails both the two-character stem and the two-character
 * extension. Only something that really reads as a file matches.
 */
const PATH_NOTATION_RE =
  /(?<![\w@./-])[\w@-]{2,}(?:\/[\w@.-]+)*\.[a-z]{2,5}(?![\w@/-])|\blines?\s+\d+/i;

/**
 * Args and structure: `{"path": "…"}`, `["a","b"]`, `key="value"`. A summary
 * carrying any of it is a call, not a sentence, and the reader gains nothing
 * from seeing the call. Deliberately keyed on QUOTES so a REST route with a
 * placeholder (`POST /{post_id}/publish`) is not mistaken for JSON.
 */
const ARGS_NOTATION_RE = /[{[]\s*["'{[]|["']\s*:|=\s*["'{[]/;

/**
 * Identifier notation: snake_case (which is also every tool id we have —
 * `read_file`, `show_diagram` — so this is the tool-name guard too),
 * camelCase, `something()`, and the `::` / `=>` / `->` operators. None of it
 * is the reader's vocabulary.
 */
const SYMBOL_NOTATION_RE =
  /\b[a-z0-9]+_[a-z0-9_]+\b|\b[a-z][a-z0-9]*[A-Z][A-Za-z0-9]*\b|\w\s*\(\s*\)|::|=>|->/;

/**
 * True when a would-be detail line carries notation instead of speech
 * (round 5: "no file paths, symbol names, JSON args, or tool names"). Route
 * placeholders come out before the identifier scan: round 2 settled that
 * `POST /{post_id}/publish` reads fine, and `post_id` inside it is route
 * grammar, not a symbol someone has to know.
 */
export function carriesNotation(text: string): boolean {
  if (ARGS_NOTATION_RE.test(text)) return true;
  return SYMBOL_NOTATION_RE.test(text.replace(/\{[^{}]*\}/g, ''));
}

/** "read_file" → "read file": a raw tool id never reaches a chip. */
function humanizeTool(tool: string): string {
  return tool.replace(/[_-]+/g, ' ').trim();
}

/**
 * A leading `label:` on a summary ("diagram: Publish, end to end") repeats
 * what the verb already said; the phrase after it is the part worth reading.
 */
const SUMMARY_LABEL_RE = /^[a-z][a-z ]{0,20}:\s+(?=\S)/i;

const FILLER = new Set(['a', 'an', 'the', 'this', 'that', 'its', 'of', 'to', 'and', 'through']);

function words(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 0 && !FILLER.has(w));
}

/**
 * True when the verb and the summary say the same thing — the common case
 * once the server speaks in general phrases ("reading" + "reading the code").
 * Then the chip shows the summary alone rather than "verb · summary".
 */
function redundant(verb: string, summary: string): boolean {
  const a = verb.toLowerCase().trim();
  const b = summary.toLowerCase().trim();
  if (a === b || b.includes(a) || a.includes(b)) return true;
  const [firstVerb] = words(a);
  const [firstSummary] = words(b);
  return firstVerb !== undefined && firstVerb === firstSummary;
}

/**
 * The chip copy the CLIENT composes from a tool frame: `text` is the line the
 * chip shows, `detail` the one line waiting behind its disclosure (null when
 * there is nothing worth opening).
 *
 * Notation never leaks into either. "reading a flow · brand-onboarding
 * (9 steps)" reads "reading the Brand Onboarding flow", step ids (s1/s2…) are
 * stripped, a summary built around a file path, an identifier or a set of
 * args drops back to the verb alone, and a summary that merely restates the
 * verb replaces it. Exported for tests.
 */
export function activityLabel(
  tool: string,
  summary: string | null,
): { text: string; detail: string | null } {
  // Round 4 (owner: "Why is [the diagram title] so highlighted? What value am
  // I supposed to get from it?"). The same long title was on the chip, on the
  // transcript's card and on the pane header. The chip already says a diagram
  // is being drawn, and the pane says which one, so the chip drops the name.
  if (tool === 'show_diagram') {
    return { text: TOOL_VERBS[tool] ?? 'drawing a diagram', detail: null };
  }
  if (summary !== null) {
    if (tool === 'get_flow') {
      const m = FLOW_SUMMARY_RE.exec(summary.trim());
      if (m !== null && m[1] !== undefined) {
        return { text: `reading the ${humanizeSlug(m[1])} flow`, detail: null };
      }
    }
    if (tool === 'canvas_step') {
      const m = STEP_SUMMARY_RE.exec(summary.trim());
      if (m !== null && m[1] !== undefined) {
        return { text: `stepping through the ${humanizeSlug(m[1])} flow`, detail: null };
      }
    }
  }
  const text = TOOL_VERBS[tool] ?? humanizeTool(tool);
  if (summary === null || summary.length === 0) return { text, detail: null };
  // A path-shaped summary is the thing the owner asked us to stop showing.
  if (PATH_NOTATION_RE.test(summary)) return { text, detail: null };
  const detail = stripStepIds(summary).replace(SUMMARY_LABEL_RE, '').trim();
  if (detail.length === 0) return { text, detail: null };
  // Notation is not a detail line; the verb alone is the honest fallback.
  if (carriesNotation(detail)) return { text, detail: null };
  if (redundant(text, detail)) return { text: detail, detail: null };
  return { text, detail };
}

/** The chip's own skin — one row, whether it is a plain span or a button.
 * Round 9 (owner: "the boxes feel too boxy"): the border and fill are gone;
 * an activity line is a quiet glyph-plus-verb row, not a pill. The working
 * pulse and the disclosure caret carry the remaining meaning. */
const CHIP_SKIN =
  'inline-flex max-w-full items-center gap-1.5 rounded-chip px-1 py-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary';

/** A disclosure caret. Two static paths: nothing rotates, nothing animates. */
function Caret({ open }: { open: boolean }) {
  return (
    <svg
      width={10}
      height={10}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 opacity-70"
      aria-hidden
    >
      {open ? <path d="m5 9 7 7 7-7" /> : <path d="m9 5 7 7-7 7" />}
    </svg>
  );
}

/**
 * One line of progress. Deliberately plain: no mono face, no code chrome —
 * these are sentences about what the guide is doing, not artifacts.
 *
 * Round 3: the leading dot became a glyph for the KIND of work (toolIcons),
 * so a run of chips reads as a sequence of different actions rather than a
 * column of identical bullets. It pulses while the tool is still running
 * and settles the moment its result lands.
 *
 * Round 5: the chip stopped saying everything at once. It used to render
 * "verb · detail" on one line; now the verb is the line and the detail waits
 * behind a disclosure — ONE short sentence about what it looked at, in the
 * reader's words. A frame whose summary is notation, or that carries nothing
 * past its verb, has no detail to show, so it renders as a plain span with
 * nothing to click: an empty disclosure is a promise the chip cannot keep.
 */
export function ActivityChip({
  tool,
  summary,
}: {
  tool: string;
  summary: string | null;
}) {
  const label = activityLabel(tool, summary);
  const [open, setOpen] = useState(false);
  const detailId = useId();
  const face = (
    <>
      <ToolGlyph tool={tool} running={summary === null} />
      <span className="min-w-0 break-words">{label.text}</span>
    </>
  );

  if (label.detail === null) {
    return (
      <span data-testid="activity-chip" data-tool={tool} className={`my-1 ${CHIP_SKIN}`}>
        {face}
      </span>
    );
  }

  return (
    <span className="my-1 flex max-w-full flex-col items-start gap-0.5">
      <button
        type="button"
        data-testid="activity-chip"
        data-tool={tool}
        data-expandable="true"
        aria-expanded={open}
        aria-controls={detailId}
        title={open ? 'Hide what this step looked at' : 'What this step looked at'}
        onClick={() => setOpen((was) => !was)}
        className={`${CHIP_SKIN} cursor-pointer border-none bg-transparent text-left transition-colors duration-(--motion-hover) hover:bg-inset hover:text-secondary`}
      >
        {face}
        <Caret open={open} />
      </button>
      <span
        id={detailId}
        data-testid="activity-detail"
        hidden={!open}
        className="pl-2 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
      >
        {label.detail}
      </span>
    </span>
  );
}

/**
 * How long the tool work took, once, at the end of the TURN (round 3: "some
 * sort of running duration would also be nice"; round 5: "Time elapsed time
 * should come at the end of the turn" — it used to ride the last chip, which
 * put it wherever the wall of chips happened to stop). It is wall-clock
 * measured on the CLIENT, from the turn's first tool.start to its last
 * tool.result, so it only exists for a turn this browser watched happen;
 * replayed history carries no timestamps and shows nothing rather than a
 * guess.
 */
export function ActivityElapsed({ ms }: { ms: number }) {
  return (
    <span
      data-testid="activity-elapsed"
      title="How long the guide spent looking things up for this answer"
      className="shrink-0 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary tabular-nums"
    >
      {formatElapsedMs(ms)}
    </span>
  );
}

/** One tool call, as the transcript fold hands it over. */
export interface ActivityStep {
  toolId: string;
  tool: string;
  summary: string | null;
}

/**
 * One thing the guide did before it answered: a tool call, or a passage of
 * its own thinking. Ordered exactly as the frames arrived, so a run reads
 * the way the work happened.
 */
export type ActivityItem =
  | ({ kind: 'step' } & ActivityStep)
  | { kind: 'thinking'; key: string; text: string };

/**
 * The model's own summary of how it is working the answer out (round 5).
 *
 * The owner watched a sixty-eight-second silence with nothing in it but
 * chips. This is what the server now streams into that gap, and it is the
 * one thing on screen that is neither the answer nor plumbing, so it has to
 * read as neither: not the transcript's 15px primary prose, not a bordered
 * chip, but plain quiet text one notch down in size and two in colour.
 *
 * Rendered verbatim, never through the markdown renderer: this is somebody
 * thinking out loud, not a document, and headings inside it would make it
 * look like an answer. Line breaks are kept, which is all the shape it has.
 */
export function ThinkingNote({ text }: { text: string }) {
  return (
    <p
      data-testid="thinking-note"
      title="How the guide is working this out. It is thinking, not an answer."
      className="my-1.5 whitespace-pre-wrap text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] text-tertiary"
    >
      {text}
    </p>
  );
}

/**
 * A turn's tool work, in one place.
 *
 * Round 5 (owner, on a real session that stacked twenty-four chips above the
 * answer): "after the answer finishes generating, it should collapse the tool
 * calls / thinking process, right? With the option to expand."
 *
 * WHILE THE TURN RUNS the chips stay exactly as they were. They are the only
 * thing happening during a sixty-eight-second wait, and a spinner that said
 * "working" instead would be a downgrade.
 *
 * ONCE IT IS DONE they are history, and history belongs behind a door: the
 * run becomes one row — "14 steps · 1m 08s" — and the answer sits where the
 * reader's eye already was. Opening it is per-turn and local; every finished
 * turn starts closed, including the ones that arrive from the server, which
 * is why nothing here is persisted.
 *
 * The run holds the model's THINKING too, in the same order it happened.
 * Same rule, for the same reason: while the turn runs the thinking is the
 * news, and once the answer is there it is the working behind the answer,
 * which belongs behind the same door as everything else that was working.
 *
 * The list keeps its element and its id in both states (`hidden`), so the
 * disclosure always controls something real and the row itself never moves.
 */
export function ActivityRun({
  items,
  elapsedMs = null,
  live = false,
}: {
  /** every tool call and thinking passage of this turn, in wire order */
  items: readonly ActivityItem[];
  /** the turn's tool time, once, on the summary row; null when unmeasured */
  elapsedMs?: number | null;
  /** true while the turn is still streaming: no door, no summary */
  live?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  if (items.length === 0) return null;

  const stepCount = items.filter((item) => item.kind === 'step').length;
  const thought = items.some((item) => item.kind === 'thinking');

  const list = (
    <div
      id={listId}
      hidden={!live && !open}
      data-testid="activity-steps"
      // Opened, the steps sit INSIDE the row that opened them: a thin rule
      // and a small indent, so a run of chips reads as the contents of the
      // disclosure rather than as more transcript. While the turn is live
      // there is no disclosure to be inside of, so there is no rule either.
      className={live ? undefined : 'ml-1 border-l border-default pl-2'}
    >
      {items.map((item) =>
        item.kind === 'thinking' ? (
          <ThinkingNote key={item.key} text={item.text} />
        ) : (
          <div key={item.toolId} className="flex flex-wrap items-center gap-x-2">
            <ActivityChip tool={item.tool} summary={item.summary} />
          </div>
        ),
      )}
    </div>
  );

  if (live) {
    return (
      <div data-testid="activity-run" data-state="live">
        {list}
      </div>
    );
  }

  return (
    <div data-testid="activity-run" data-state={open ? 'expanded' : 'collapsed'}>
      <button
        type="button"
        data-testid="activity-summary"
        aria-expanded={open}
        aria-controls={listId}
        title={
          thought
            ? 'What the guide was working out before it answered'
            : 'What the guide looked up before answering'
        }
        onClick={() => setOpen((was) => !was)}
        className={`my-1 ${CHIP_SKIN} cursor-pointer text-left transition-colors duration-(--motion-hover) hover:border-strong hover:text-secondary`}
      >
        <Caret open={open} />
        {/* A run that only thought has no steps to count, and "0 steps" would
            be a lie about a turn that did plenty. It says what it holds. */}
        <span>{stepCount > 0 ? plural(stepCount, 'step') : 'Thinking'}</span>
        {elapsedMs !== null ? (
          <>
            <span aria-hidden>·</span>
            <ActivityElapsed ms={elapsedMs} />
          </>
        ) : null}
      </button>
      {list}
    </div>
  );
}

/** The wire prefixes on infra ids; they type the id, they do not name it. */
const ENTITY_PREFIX_RE = /^(?:svc|queue|sched|ext):/;

/**
 * `svc:sunrise-worker` reads as "sunrise-worker" (round 2, item D: the
 * transcript is a conversation, not a build log). The full id stays on the
 * element — title and `data-entity-id` — so nothing downstream loses it.
 */
export function entityDisplayName(id: string): string {
  return id.replace(ENTITY_PREFIX_RE, '');
}

/**
 * A typed entity mention. The map it used to drive is gone, so the chip does
 * the thing that is still useful: it stages the question. Clicking one drops
 * "What is <name>?" into the composer, ready to send or edit.
 */
export function EntityChip({ entity }: { entity: EntityRef }) {
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);
  const name = entityDisplayName(entity.id);

  return (
    <button
      type="button"
      data-testid="entity-chip"
      data-entity-kind={entity.kind}
      data-entity-id={entity.id}
      onClick={() => setComposerDraft(`What is ${name}, and what depends on it?`)}
      title={`Ask about ${entity.id}`}
      className="mono inline cursor-pointer rounded-chip border border-default bg-inset px-1 py-0.5 text-(--accent) transition-colors duration-(--motion-hover) hover:border-(--accent)"
    >
      {name}
    </button>
  );
}

/**
 * A file citation (`file.py:41`). The code-peek panel it used to open went
 * with the map, so the citation stages the ask instead: the guide quotes the
 * lines in the conversation, where the rest of the answer already is.
 */
export function FileChip({
  path,
  line,
  label,
}: {
  path: string;
  line: number | null;
  label: string;
}) {
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);
  const where = line !== null ? `${path}:${line}` : path;
  const open = () => {
    setComposerDraft(`Show me what happens in ${where}.`);
  };
  return (
    <button
      type="button"
      data-testid="file-chip"
      onClick={open}
      title={`Ask about ${where}`}
      className="mono inline cursor-pointer rounded-chip border border-default bg-inset px-1 py-0.5 text-secondary transition-colors duration-(--motion-hover) hover:border-(--accent) hover:text-primary"
    >
      {label}
    </button>
  );
}
